// frontend/src/components/grants/GrantsPanel.tsx
import { useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/services/api';
import { Link } from 'react-router-dom';
import { grantMatches, isLinkableClaim, type GrantScope } from '@/lib/bills-display';
import { isNotApprovedGrant, isPastGrant, todoCountByGrant } from '@/lib/grants-status';
import { renewalGrantInSetup, signatureGrantInSetup } from '@/lib/grants-display';
import { RENEWAL_MINE_QUERY_KEY } from '@/lib/wizardDataSource';
import { GrantRow } from './GrantRow';
import { GrantReceipts } from './GrantReceipts';
import type { GrantRequest, ReimbursementClaim, ReimbursementClaimLite } from '@/types/api';

/**
 * The family's grants: ONE LINE per grant, opening to that grant's receipts.
 *
 * Rebuilt 2026-09-21 (spec 2026-09-21-parent-chrome-and-grants-design.md
 * Part 3). It was a stack of cards, each carrying its own heading, chip, bar
 * and receipt list — readable one at a time, impossible to compare down the
 * page, which is the comparison a family actually wants ("how much is left
 * on each of these?"). Rows compare, cards browse (§1.3).
 *
 * `GrantRow` is the line and carries the money; `GrantReceipts` is the
 * expanded body and carries the breakdown, the linked claims and the
 * link/unlink controls. This file keeps the queries, the mutations, the
 * tri-state and the props — every caller and every filter behaves as before.
 *
 * Its only mount is `/bills`, which is now one page rather than a tab set;
 * `/grants` redirects there.
 *
 * Tri-state (AP-58): on the grants tab the old `no rows → null`
 * collapse would render loading, errored and genuinely-empty as the same blank
 * page. Loading pulses, an error says so with a retry, and empty says "no
 * grants yet" — three different facts, three different renders. The claims
 * query stays secondary: if it fails, grants still render and the link picker
 * simply offers nothing (linking is retryable chrome, not a money fact).
 *
 * Toolbar search + scope (2026-09-10 declutter, spec §3) apply client-side
 * over the loaded grants with the shared `grantMatches`; both default to
 * "everything", so a bare `<GrantsPanel />` renders as it always did. The
 * sentence for "the toolbar hid every grant" comes IN from the page as
 * `emptyMatch`: the page owns the toolbar state that causes it, and it keeps
 * that wording beside its "No upcoming expenses match." sibling. (Registering
 * it here as a copy slot would trip Mario's 2026-08-29 condition that every
 * slot is `apply.*` — `copySlots.verify.test.ts` — and `components/grants`
 * is a copy-covered directory, so the literal cannot live in this file.)
 *
 * Live grants first, denied and withdrawn ones folded under a past fold
 * (2026-10-03). A Denied row sitting above the Approved one made the first
 * thing a family read about their money a refusal. The family wire carries no
 * `decision_reason`, so the fold offers help instead of a reason. The fold
 * reads "Past grant requests" (2026-10-11, was "Past grants"): a denial is a
 * request that never became a grant, and the es "Apoyo económico anterior"
 * read as support the family had received.
 */
export interface GrantsPanelProps {
  search?: string;
  statusScope?: GrantScope;
  /** Rendered when the family HAS grants and search/scope hides every one. */
  emptyMatch?: ReactNode;
}

export function GrantsPanel({ search = '', statusScope = 'all', emptyMatch = null }: GrantsPanelProps = {}) {
  const { t } = useTranslation(['grants', 'common']);
  const qc = useQueryClient();
  const grantsQuery = useQuery({ queryKey: ['grants-mine'], queryFn: () => api.grants.mine() });
  const { data: claimsRes } = useQuery({ queryKey: ['reimbursements-mine'], queryFn: () => api.reimbursements.listMine() });
  // #2745: the renewal read, to name an approved renewal's grant while staff
  // have not Decided it. Same key and options as /bills' renewal line, so a
  // cache hit there. A failed or 404 read names nothing: every row reads as before.
  const renewalQuery = useQuery({
    queryKey: RENEWAL_MINE_QUERY_KEY,
    queryFn: () => api.renewals.getMine(),
    retry: false,
  });
  const grants = (grantsQuery.data?.data ?? []) as GrantRequest[];
  const renewalSetupId = renewalQuery.isSuccess ? renewalGrantInSetup(grants, renewalQuery.data?.data ?? null) : null;
  // L5-a (D6): without an approved renewal to name, the newest undecided
  // Signature grant still says TRF is setting it up. Admission creates that
  // grant `submitted`, so every newly admitted family reads this line until
  // staff Decide it. No renewal precondition, so a pending, failed or 404
  // renewal read still explains the row.
  const awardSetupId = renewalSetupId ? null : signatureGrantInSetup(grants);
  const claims = (claimsRes?.data ?? []) as ReimbursementClaimLite[];
  // A handful of rows; no memo needed, and none wanted — `grants` is a `??`
  // expression, which react-hooks would flag as an unstable dependency.
  const visibleGrants = grants.filter((g) => grantMatches(g, statusScope, search));

  // One grant open at a time (2026-09-21). The list is the page's primary
  // reading; an accordion that could hold every grant open would turn it
  // back into the stack of cards this replaced.
  const [openId, setOpenId] = useState<string | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['grants-mine'] });
    qc.invalidateQueries({ queryKey: ['reimbursements-mine'] });
  };
  const link = useMutation({
    mutationFn: ({ grantId, claimId }: { grantId: string; claimId: string }) => api.grants.linkReceipt(grantId, claimId),
    onSuccess: invalidate,
  });
  const unlink = useMutation({
    mutationFn: ({ grantId, claimId }: { grantId: string; claimId: string }) => api.grants.unlinkReceipt(grantId, claimId),
    onSuccess: invalidate,
  });

  if (grantsQuery.isPending) {
    // Shared Skeleton, not a new animate-pulse literal — the motion ratchet
    // (grammar-guards) caps indefinite-loop animations and the skeleton's one
    // pulse is already counted centrally (D-W2-4: loading = skeleton).
    return (
      <section data-testid="grants-panel" className="space-y-3" aria-busy="true">
        <Skeleton className="h-24 rounded-lg" />
        <Skeleton className="h-24 rounded-lg" />
      </section>
    );
  }

  if (grantsQuery.isError) {
    return (
      <section data-testid="grants-panel">
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 flex items-center gap-3">
          <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
          <p className="text-body text-foreground flex-1">
            {t('panel.loadFailed')}
          </p>
          <Button variant="outline" size="sm" onClick={() => grantsQuery.refetch()}>
            {t('common:actions.retry')}
          </Button>
        </div>
      </section>
    );
  }

  if (grants.length === 0) {
    return (
      <section data-testid="grants-panel">
        <p className="text-body text-stevens-ink-3">
          {t('panel.empty')}
        </p>
      </section>
    );
  }

  // A fourth fact, distinct from the three above: the family HAS grants and the
  // toolbar's search or scope hid every one of them.
  if (visibleGrants.length === 0) {
    return <section data-testid="grants-panel">{emptyMatch}</section>;
  }

  // Unlinked, general receipts available to attach.
  // A draft is still the family's (no receipt, or details missing) — not yet a receipt to link.
  const available = claims.filter(isLinkableClaim);

  const todo = todoCountByGrant(claims as ReimbursementClaim[]);
  const notApproved = visibleGrants.filter(isNotApprovedGrant);
  const past = visibleGrants.filter(isPastGrant);
  const live = visibleGrants.filter((g) => !isPastGrant(g) && !isNotApprovedGrant(g));

  const rows = (list: GrantRequest[]) => (
    <ul className="divide-y rounded-lg border">
      {list.map((g) => (
        <li key={g._id}>
          <GrantRow
            grant={g}
            expanded={openId === g._id}
            onToggle={() => setOpenId(openId === g._id ? null : g._id)}
            todoCount={todo.get(g._id) ?? 0}
            renewalInSetup={g._id === renewalSetupId}
            awardInSetup={g._id === awardSetupId}
          />
          {openId === g._id && (
            <GrantReceipts
              grant={g}
              available={available}
              onLink={(claimId) => link.mutate({ grantId: g._id, claimId })}
              onUnlink={(claimId) => unlink.mutate({ grantId: g._id, claimId })}
              busy={link.isPending || unlink.isPending}
            />
          )}
        </li>
      ))}
    </ul>
  );

  return (
    <section data-testid="grants-panel" className="space-y-3">
      {live.length > 0 && rows(live)}
      {/* #2533: a denied request was never a grant, so it is not a "past
          grant" — it gets its own small group, with the help line. Each fold
          opens by itself when it is all there is, so the list is never blank. */}
      {notApproved.length > 0 && (
        <details data-testid="not-approved-grants" open={live.length === 0 || undefined} className="group">
          <summary className="cursor-pointer text-body-sm text-stevens-ink-3 hover:text-foreground">
            {t('page.notApprovedGrants.title', { count: notApproved.length })}
          </summary>
          <div className="mt-2 space-y-2">
            {rows(notApproved)}
            <p className="text-body-sm text-stevens-ink-3">
              {t('page.pastGrants.help')}{' '}
              <Link to="/help" className="text-stevens-indigo hover:underline">{t('page.pastGrants.helpLink')}</Link>
            </p>
          </div>
        </details>
      )}
      {past.length > 0 && (
        <details data-testid="past-grants" open={(live.length === 0 && notApproved.length === 0) || undefined} className="group">
          <summary className="cursor-pointer text-body-sm text-stevens-ink-3 hover:text-foreground">
            {t('page.pastGrants.title', { count: past.length })}
          </summary>
          <div className="mt-2 space-y-2">
            {rows(past)}
          </div>
        </details>
      )}
    </section>
  );
}
